import { useCallback, useEffect, useRef, useState } from 'react';

/** Platform admin idle sign-out (D-72): 30 minutes without activity. */
export const ADMIN_IDLE_LIMIT_MS = 30 * 60 * 1000;

/** Warning dialog one minute before sign-out. */
export const ADMIN_IDLE_WARNING_LEAD_MS = 60 * 1000;

export interface IdleClock {
  clearTimeout(handle: ReturnType<typeof globalThis.setTimeout>): void;
  now(): number;
  setTimeout(callback: () => void, delayMs: number): ReturnType<typeof globalThis.setTimeout>;
}

export const systemIdleClock: IdleClock = {
  clearTimeout: (handle) => globalThis.clearTimeout(handle),
  now: () => Date.now(),
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
};

export type IdlePhase = 'active' | 'warning';

export function useIdleTimeout({
  clock = systemIdleClock,
  enabled,
  limitMs,
  onTimeout,
  warningLeadMs,
}: {
  readonly clock?: IdleClock;
  readonly enabled: boolean;
  readonly limitMs: number;
  readonly onTimeout: () => void;
  readonly warningLeadMs: number;
}): {
  readonly continueSession: () => void;
  readonly phase: IdlePhase;
  readonly signOutNow: () => void;
} {
  const lastActivityAt = useRef(clock.now());
  const warningTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const [phase, setPhase] = useState<IdlePhase>('active');

  const clearTimers = useCallback(() => {
    if (warningTimer.current !== null) {
      clock.clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
    if (logoutTimer.current !== null) {
      clock.clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
  }, [clock]);

  const schedule = useCallback(() => {
    clearTimers();
    const elapsed = clock.now() - lastActivityAt.current;
    const warningAt = limitMs - warningLeadMs;
    const remainingLogout = limitMs - elapsed;
    const remainingWarning = warningAt - elapsed;

    if (remainingLogout <= 0) {
      setPhase('active');
      onTimeoutRef.current();
      return;
    }

    if (remainingWarning <= 0) {
      setPhase('warning');
    } else {
      setPhase('active');
      warningTimer.current = clock.setTimeout(() => {
        setPhase('warning');
      }, remainingWarning);
    }

    logoutTimer.current = clock.setTimeout(() => {
      setPhase('active');
      onTimeoutRef.current();
    }, remainingLogout);
  }, [clock, clearTimers, limitMs, warningLeadMs]);

  const noteActivity = useCallback(() => {
    lastActivityAt.current = clock.now();
    setPhase('active');
    schedule();
  }, [clock, schedule]);

  const continueSession = useCallback(() => {
    noteActivity();
  }, [noteActivity]);

  const signOutNow = useCallback(() => {
    clearTimers();
    setPhase('active');
    onTimeoutRef.current();
  }, [clearTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setPhase('active');
      return undefined;
    }

    lastActivityAt.current = clock.now();
    schedule();

    const onPointer = () => noteActivity();
    const onKey = () => noteActivity();
    const onTouch = () => noteActivity();
    // A hidden tab throttles timers, so on return the elapsed idle time is checked first: past the
    // limit signs out at once; otherwise the timers are rescheduled from the last real activity
    // (returning to the tab is not activity by itself).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        schedule();
      }
    };

    document.addEventListener('pointerdown', onPointer, { passive: true });
    document.addEventListener('keydown', onKey);
    document.addEventListener('touchstart', onTouch, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimers();
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('touchstart', onTouch);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clearTimers, clock, enabled, noteActivity, schedule]);

  return { continueSession, phase, signOutNow };
}
